<!-- Inline visualisation of a tool result (chat functionality, F1). When an
     analytics/query tool returns a stats summary (summarize_window) or a time
     series (query_series), render a compact KPI strip + SVG sparkline above the
     raw JSON — so the answer is visual, not a wall of numbers. Shape-tolerant:
     it sniffs the result, renders nothing when there's nothing to plot. -->
<template>
  <div v-if="stats || points.length >= 2" class="rounded-lg border border-line bg-surface px-3 py-2">
    <div v-if="stats" class="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-xs">
      <span v-if="stats.last != null" class="flex items-baseline gap-1">
        <span class="text-faint">último</span><span class="text-sm font-semibold text-ink">{{ fmt(stats.last) }}</span>
      </span>
      <span class="text-faint">avg <b class="text-ink">{{ fmt(stats.avg) }}</b></span>
      <span class="text-faint">min <b class="text-ink">{{ fmt(stats.min) }}</b></span>
      <span class="text-faint">max <b class="text-ink">{{ fmt(stats.max) }}</b></span>
      <span v-if="stats.pct != null" :style="{ color: trendColor || undefined }">{{ stats.pct >= 0 ? '+' : '' }}{{ fmt(stats.pct) }}%</span>
      <span v-if="stats.trend" class="flex items-center gap-1 font-medium" :style="{ color: trendColor || undefined }">{{ arrow }} {{ stats.trend }}</span>
    </div>
    <svg v-if="points.length >= 2" class="mt-1.5 block w-full" :viewBox="`0 0 ${W} ${H}`" preserveAspectRatio="none" :style="{ height: '34px' }">
      <polyline :points="poly" fill="none" :stroke="trendColor || '#4f46e5'" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" />
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const props = defineProps<{ output: unknown }>();
const W = 240;
const H = 34;

const parsed = computed<Record<string, unknown> | null>(() => {
  let o = props.output;
  if (typeof o === 'string') { try { o = JSON.parse(o); } catch { return null; } }
  return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
});

const stats = computed(() => {
  const o = parsed.value;
  if (!o || typeof o.avg !== 'number' || typeof o.min !== 'number' || typeof o.max !== 'number') return null;
  return {
    avg: o.avg, min: o.min, max: o.max,
    last: typeof o.last === 'number' ? o.last : null,
    pct: typeof o.pct_change === 'number' ? o.pct_change : null,
    trend: typeof o.trend === 'string' ? o.trend : null,
  };
});

// Pull a plottable value series out of common result shapes: an explicit
// `series`/`points` array, or the last numeric column of a long-format `rows`
// table ([ts, …, value]). Non-numeric rows (list_* tools) yield nothing.
const points = computed<number[]>(() => {
  const o = parsed.value;
  if (!o) return [];
  const cand = o.series ?? o.points ?? o.rows;
  if (!Array.isArray(cand)) return [];
  const vals = cand
    .map((r): number => {
      if (typeof r === 'number') return r;
      if (Array.isArray(r)) { const v = r[r.length - 1]; return typeof v === 'number' ? v : NaN; }
      if (r && typeof r === 'object') { const v = (r as Record<string, unknown>).value ?? (r as Record<string, unknown>).v ?? (r as Record<string, unknown>).y; return typeof v === 'number' ? v : NaN; }
      return NaN;
    })
    .filter((v) => Number.isFinite(v));
  return vals.length >= 2 && vals.length <= 5000 ? vals : [];
});

const trendColor = computed(() => {
  const s = stats.value;
  if (s?.trend === 'rising') return '#16a34a';
  if (s?.trend === 'falling') return '#dc2626';
  if (s?.pct != null) return s.pct >= 0 ? '#16a34a' : '#dc2626';
  return '';
});
const arrow = computed(() => (stats.value?.trend === 'rising' ? '↑' : stats.value?.trend === 'falling' ? '↓' : '→'));

const poly = computed(() => {
  const v = points.value;
  if (v.length < 2) return '';
  const min = Math.min(...v), max = Math.max(...v), span = max - min || 1;
  return v.map((y, i) => `${((i / (v.length - 1)) * W).toFixed(1)},${(H - ((y - min) / span) * (H - 4) - 2).toFixed(1)}`).join(' ');
});

function fmt(n: number): string {
  return Math.abs(n) >= 1000 ? n.toLocaleString(undefined, { maximumFractionDigits: 1 }) : n.toFixed(2);
}
</script>
